import { useCallback } from "react";
import useFormEvents from "@/hooks/eventing/useFormtEvents";
import { FieldSelected } from "@/hooks/eventing/types/redventures.usertracking.v3.FieldSelected";
import { FieldViewed } from "@/hooks/eventing/types/core.fieldViewed.v1";
import { EVENTS } from "@/hooks/eventing/types/events";
import makeEvent from "@/hooks/eventing/makeEvent";
import { getCorrelationID, getPageType } from "@/utils/tagular/helpers";

// Form context for all field events
const FORM_CONTEXT = {
  formName: "get-started-with",
  formType: "get-started-with",
  formVersion: "flow",
};

// Element constants for CTA events
export const GET_STARTED_ELEMENT_LOCATION = "get-started-with";
export const GET_STARTED_ELEMENT_TYPE = "BUTTON";
export const GET_STARTED_ELEMENT_HTML_ID = "get-started-with-cta";

// Element names
export const ELEMENT_NAMES = {
  EXPLORE_COURSES: "explore-courses",
  EDIT_SELECTIONS: "edit-selections",
  VIEW_ALL_PRODUCTS: "view-all-products",
} as const;

// Field viewed event config
export const FIELD_VIEWED_CONFIG = {
  fieldName: "get-started-with",
  fieldLabel: "Get Started With",
  fieldType: "Drop-down / search bar",
  fieldNumber: 1,
} as const;

type FieldType = "Subject" | "Skill";

interface UseGetStartedWithFieldEventsReturn {
  trackFieldSelected: (fieldType: FieldType, fieldValue: string) => void;
  trackFieldViewed: () => void;
}

const getWebContext = () => {
  if (typeof window === "undefined") return undefined;
  return {
    page: {
      title: document.title,
      url: window.location.href,
      pageType: getPageType(),
    },
  };
};

export const useGetStartedWithFieldEvents =
  (): UseGetStartedWithFieldEventsReturn => {
    const { fieldSelected } = useFormEvents({});

    const trackFieldSelected = useCallback(
      (fieldType: FieldType, fieldValue: string) => {
        if (typeof window === "undefined") return;

        const formEvent: FieldSelected = {
          correlationId: getCorrelationID(),
          formContext: FORM_CONTEXT,
          userInputField: {
            fieldName: fieldType,
            fieldType: fieldType,
            fieldValue: fieldValue,
          },
          webContext: getWebContext(),
          "@type": "redventures.usertracking.v3.FieldSelected",
        };

        fieldSelected(formEvent);
      },
      [fieldSelected],
    );

    const trackFieldViewed = useCallback(() => {
      if (typeof window === "undefined") return;

      const fieldViewedEvent: FieldViewed = {
        correlationId: getCorrelationID(),
        formContext: {
          formId: FORM_CONTEXT.formName,
          formName: FORM_CONTEXT.formName,
          formType: FORM_CONTEXT.formType,
          formVersion: FORM_CONTEXT.formVersion,
        },
        userInputField: {
          fieldName: FIELD_VIEWED_CONFIG.fieldName,
          fieldType: FIELD_VIEWED_CONFIG.fieldType,
          fieldValue: "",
          fieldNumber: FIELD_VIEWED_CONFIG.fieldNumber,
          fieldLabel: FIELD_VIEWED_CONFIG.fieldLabel,
        },
        "@type": "core.fieldViewed.v1",
      };

      const event = makeEvent<FieldViewed, EVENTS>(fieldViewedEvent);
      const eventHandler = event<FieldViewed>(EVENTS.FieldViewed);
      eventHandler();
    }, []);

    return {
      trackFieldSelected,
      trackFieldViewed,
    };
  };

export default useGetStartedWithFieldEvents;
